import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquareIcon, SearchIcon, FilterIcon, PlusIcon, ClockIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, ChevronRightIcon, UserIcon, MapPinIcon, CalendarIcon } from 'lucide-react';
// Mock enquiries data
const enquiriesData = [{
  id: 1,
  customer: {
    name: 'Jennifer Williams',
    email: 'jwilliams@example.com',
    phone: '+1 (555) 987-6543'
  },
  destination: 'Caribbean Cruise',
  travelDates: 'Aug 5 - Aug 12, 2023',
  budget: '$2,000 - $3,000',
  travelers: '2 adults',
  status: 'new',
  priority: 'high',
  message: 'Looking for a relaxing cruise package for our anniversary.',
  dateReceived: '2023-06-10T14:30:00',
  assignedTo: 'Sarah Johnson',
  lastActivity: '2023-06-10T14:30:00'
}, {
  id: 2,
  customer: {
    name: 'Robert Chen',
    email: 'robert.chen@example.com',
    phone: '+1 (555) 123-4567'
  },
  destination: 'Japan',
  travelDates: 'Oct 15 - Oct 28, 2023',
  budget: '$5,000 - $7,000',
  travelers: '2 adults, 1 child',
  status: 'in-progress',
  priority: 'medium',
  message: 'Interested in a family-friendly cultural tour of Japan with some time in Tokyo and Kyoto.',
  dateReceived: '2023-06-08T09:15:00',
  assignedTo: 'Sarah Johnson',
  lastActivity: '2023-06-09T11:45:00'
}, {
  id: 3,
  customer: {
    name: 'Michael Torres',
    email: 'mtorres@example.com',
    phone: '+1 (555) 456-7890'
  },
  destination: 'European Tour',
  travelDates: 'Sep 12 - Sep 26, 2023',
  budget: '$4,000 - $6,000',
  travelers: '1 adult',
  status: 'quoted',
  priority: 'medium',
  message: 'Looking for a self-guided tour of major European cities with focus on historical sites.',
  dateReceived: '2023-06-05T16:20:00',
  assignedTo: 'Sarah Johnson',
  lastActivity: '2023-06-07T10:30:00'
}, {
  id: 4,
  customer: {
    name: 'Emily Wilson',
    email: 'ewilson@example.com',
    phone: '+1 (555) 789-0123'
  },
  destination: 'Bali',
  travelDates: 'Jul 20 - Jul 27, 2023',
  budget: '$3,000 - $4,000',
  travelers: '2 adults',
  status: 'booked',
  priority: 'low',
  message: 'Honeymoon trip to Bali. Looking for luxury accommodations and romantic activities.',
  dateReceived: '2023-05-28T11:10:00',
  assignedTo: 'David Miller',
  lastActivity: '2023-06-02T15:45:00'
}, {
  id: 5,
  customer: {
    name: 'James Rodriguez',
    email: 'jrodriguez@example.com',
    phone: '+1 (555) 234-5678'
  },
  destination: 'African Safari',
  travelDates: 'Nov 10 - Nov 20, 2023',
  budget: '$8,000 - $12,000',
  travelers: '4 adults',
  status: 'closed',
  priority: 'low',
  message: 'Group of friends interested in a luxury safari experience in Tanzania or Kenya.',
  dateReceived: '2023-05-15T08:45:00',
  assignedTo: 'Sarah Johnson',
  lastActivity: '2023-05-20T09:30:00'
}];
export const EnquiriesModule = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  // Filter enquiries based on search term and filters
  const filteredEnquiries = enquiriesData.filter(enquiry => {
    // Apply status filter
    if (statusFilter !== 'all' && enquiry.status !== statusFilter) return false;
    // Apply priority filter
    if (priorityFilter !== 'all' && enquiry.priority !== priorityFilter) return false;
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return enquiry.customer.name.toLowerCase().includes(term) || enquiry.destination.toLowerCase().includes(term) || enquiry.message.toLowerCase().includes(term);
    }
    return true;
  });
  const getStatusBadge = status => {
    switch (status) {
      case 'new':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2414">
            <ClockIcon className="h-3 w-3 mr-1" data-id="element-2415" />
            New
          </span>;
      case 'in-progress':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2416">
            <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-2417" />
            In Progress
          </span>;
      case 'quoted':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-2418">
            <MessageSquareIcon className="h-3 w-3 mr-1" data-id="element-2419" />
            Quoted
          </span>;
      case 'booked':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2420">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-2421" />
            Booked
          </span>;
      case 'closed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2422">
            <XCircleIcon className="h-3 w-3 mr-1" data-id="element-2423" />
            Closed
          </span>;
      default:
        return null;
    }
  };
  const getPriorityBadge = priority => {
    switch (priority) {
      case 'high':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-2424">
            High
          </span>;
      case 'medium':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2425">
            Medium
          </span>;
      case 'low':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2426">
            Low
          </span>;
      default:
        return null;
    }
  };
  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const handleEnquiryClick = id => {
    navigate(`/enquiries/${id}`);
  };
  return <div className="space-y-6" data-id="element-2427">
      <div className="flex justify-between items-center" data-id="element-2428">
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-2429">Enquiries</h1>
        <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => navigate('/enquiries/new')} data-id="element-2430">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-2431" />
          New Enquiry
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex space-x-4" data-id="element-2432">
        <div className="relative flex-1" data-id="element-2433">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2434">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2435" />
          </div>
          <input type="text" placeholder="Search enquiries..." className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2436" />
        </div>
        <select className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-2437">
          <option value="all" data-id="element-2438">All Statuses</option>
          <option value="new" data-id="element-2439">New</option>
          <option value="in-progress" data-id="element-2440">In Progress</option>
          <option value="quoted" data-id="element-2441">Quoted</option>
          <option value="booked" data-id="element-2442">Booked</option>
          <option value="closed" data-id="element-2443">Closed</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} data-id="element-2444">
          <option value="all" data-id="element-2445">All Priorities</option>
          <option value="high" data-id="element-2446">High</option>
          <option value="medium" data-id="element-2447">Medium</option>
          <option value="low" data-id="element-2448">Low</option>
        </select>
        <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2449">
          <FilterIcon className="h-4 w-4 mr-2" data-id="element-2450" />
          More Filters
        </button>
      </div>

      {/* Enquiries list */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-2451">
        {filteredEnquiries.length === 0 ? <div className="p-8 text-center" data-id="element-2452">
            <MessageSquareIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" data-id="element-2453" />
            <h3 className="text-lg font-medium text-gray-800 mb-2" data-id="element-2454">
              No enquiries found
            </h3>
            <p className="text-gray-500 mb-4" data-id="element-2455">
              Try adjusting your search or filters
            </p>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => {
          setSearchTerm('');
          setStatusFilter('all');
          setPriorityFilter('all');
        }} data-id="element-2456">
              Reset Filters
            </button>
          </div> : <div className="overflow-x-auto" data-id="element-2457">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2458">
              <thead className="bg-gray-50" data-id="element-2459">
                <tr data-id="element-2460">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2461">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2462">
                    Destination
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2463">
                    Travel Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2464">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2465">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2466">
                    Date Received
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2467">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2468">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2469">
                {filteredEnquiries.map(enquiry => <tr key={enquiry.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleEnquiryClick(enquiry.id)} data-id="element-2470">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2471">
                      <div className="flex items-center" data-id="element-2472">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-2473">
                          <UserIcon className="h-5 w-5" data-id="element-2474" />
                        </div>
                        <div className="ml-4" data-id="element-2475">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2476">
                            {enquiry.customer.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-2477">
                            {enquiry.customer.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2478">
                      <div className="flex items-center" data-id="element-2479">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-2480" />
                        <span className="text-sm text-gray-900" data-id="element-2481">
                          {enquiry.destination}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2482">
                      <div className="flex items-center" data-id="element-2483">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-2484" />
                        <span className="text-sm text-gray-900" data-id="element-2485">
                          {enquiry.travelDates}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2486">
                      {getStatusBadge(enquiry.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2487">
                      {getPriorityBadge(enquiry.priority)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2488">
                      {formatDate(enquiry.dateReceived)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2489">
                      {enquiry.assignedTo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2490">
                      <button className="text-blue-600 hover:text-blue-900" data-id="element-2491">
                        <ChevronRightIcon className="h-5 w-5" data-id="element-2492" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
};